import type { routes, SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { AwcLegendItems } from 'components/AwcLegendItems/AwcLegendItems'
import { lineChartTooltipFormatter } from 'components/StackedChart/_utils/lineChartTooltipFormatter/lineChartTooltipFormatter'
import type { StackedChartSectionItem, TChartSeries } from 'components/StackedChart/types'
import type { AxisLabelsFormatterContextObject } from 'highcharts'
import { SOIL_MOISTURE_COLORS_ARRAY } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { getXDateFormat } from '../../_utils/getXDateFormat'
import { soilPlotOptions } from '../soilPlotOptions'
import { makeAWCZones } from './awcZones'
import { checkIfSoilChartHasData } from './checkIfSoilChartHasData'
import { getAwcChartMinMax } from './getAwcChartMinMax'
import { IrrigationEventsLegendItems } from './IrrigationEventsLegendItems'
import { makeSoilSeries } from './makeSoilSeries'

export const makeAwcChart = ({
  data,
  compareSeasonsData,
  soilStationLngLat,
  aggregationInterval,
  availableEmittersForStation,
  hasRain,
  hasWaterEvents,
  chartId,
  compareSeasonsInterval,
  firstForecastTimestamp,
  soilStationFavoriteDepths,
  showSelectedSoilDepthsOverride,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
  soilStationLngLat: string | null
  aggregationInterval: string
  availableEmittersForStation: SharedTypes.IrrigationScheduler.TIrrigationZoneEmitterId[]
  hasRain: boolean
  hasWaterEvents: boolean
  chartId: string
  compareSeasonsInterval: number
  firstForecastTimestamp: number
  soilStationFavoriteDepths: SharedTypes.TSoilSensorDepth[]
  showSelectedSoilDepthsOverride?: boolean
}): StackedChartSectionItem => {
  const soilMoisture = data?.points?.[String(soilStationLngLat)]?.values?.soilMoisture || []

  const chartHasSomeData =
    checkIfSoilChartHasData(soilMoisture) ||
    checkIfSoilChartHasData(compareSeasonsData?.points?.[String(soilStationLngLat)]?.values?.soilMoisture)

  const soilAwcLineData = makeSoilSeries({
    data,
    compareSeasonsData,
    soilStationLngLat,
    valueType: 'soilMoisture',
    soilStationFavoriteDepths,
    showSelectedSoilDepthsOverride,
  })

  const awcZones =
    chartHasSomeData && soilMoisture[0]
      ? makeAWCZones({ awcData: soilMoisture, forecastStartsAt: firstForecastTimestamp })
      : null

  const soilAwcLineSeriesWithZones =
    awcZones && soilAwcLineData.length > 0
      ? ([
          ...soilAwcLineData,
          ...awcZones.zonesAWCValues,
          ...awcZones.linesAWCValues,
          ...awcZones.forecastedLinesAWCValues,
          ...awcZones.forecastedZonesAWCValues,
        ] as TChartSeries[])
      : []

  const { yAxisMinToUse, yAxisMaxToUse } = getAwcChartMinMax({
    compareSeasonsData,
    data,
    soilStationLngLat,
  })

  const showSelectedSoilDepths =
    showSelectedSoilDepthsOverride ?? detailsPanelStore.getState().showSelectedSoilDepths

  return {
    chartConfig: {
      semiosHighchartsAdditions: {
        id: chartId,
        firstForecastTimestamp,
      },
      chart: {
        type: 'line',
        marginTop: 5,
        // tooltip is too big with compare seasons, make chart bigger to accommodate
        height: compareSeasonsInterval ? 350 : 250,
      },
      colors: SOIL_MOISTURE_COLORS_ARRAY,
      series: soilAwcLineSeriesWithZones,
      plotOptions: soilPlotOptions,
      xAxis: { plotBands: [{ label: { text: '' } }] }, // fixes a weird highcharts bug where the Forecast label is not removed
      tooltip: {
        formatter: function (tooltip) {
          if (!this.x || (this.x as number) < firstForecastTimestamp) {
            return lineChartTooltipFormatter(
              this,
              tooltip,
              firstForecastTimestamp,
              // filter out predicted AWC when in the past
              (s) => s.userOptions.id !== 'forecasted-soilMoisture',
            )
          } else {
            return lineChartTooltipFormatter(
              this,
              tooltip,
              firstForecastTimestamp,
              (s) =>
                compareSeasonsInterval > 0 ||
                // filter out average AWC and all depth series when in the future
                (s.userOptions.id !== 'averageSelected-soilMoisture' &&
                  !s.userOptions.id.includes('in-soilMoisture') &&
                  !s.userOptions.id.includes('mm-soilMoisture')),
            )
          }
        },
        xDateFormat: getXDateFormat(aggregationInterval === 'DAILY'),
      },
      yAxis: {
        gridLineColor: 'transparent',
        min: yAxisMinToUse,
        max: yAxisMaxToUse,
        tickInterval: 10,
        labels: {
          formatter: function (this: AxisLabelsFormatterContextObject) {
            if (this.value === 0 || this.value === 50 || this.value === 100 || this.value === 150) {
              return `${this.value}`
            } else {
              return ''
            }
          },
        },
        startOnTick: false,
        endOnTick: false,
      },
      legend: {
        enabled: showSelectedSoilDepths,
      },
    },
    childrenUpper: (
      <IrrigationEventsLegendItems
        emitters={availableEmittersForStation}
        hasRain={hasRain}
        hasWaterEvents={hasWaterEvents}
      />
    ),
    childrenLower: awcZones && (
      <AwcLegendItems
        legendItems={awcZones.zonesAWCValues}
        seriesLegendOn={!!showSelectedSoilDepths}
        displayRootZoneWarning={soilStationFavoriteDepths.length === 0}
      />
    ),
  }
}
